/**
 * Карточка показателя блоков «Экономика региона» и «Цены, труд и доходы
 * населения» (US-35.1 FR-4…FR-6, выдача US-36.1).
 *
 * Одна форма на оба блока: шапка «название + свой отчётный период», далее
 * таблица «Показатель / Значение» из величин ответа, под ней — база
 * индекса и источник.
 *
 * Экран ничего не додумывает и не считает (FR-9.4):
 *  - подпись периода — готовая строка `period.label_*`, у каждой карточки
 *    своя: публикации выходят в разные дни (FR-4.7);
 *  - единица — `unit_*` величины, а не догадка по измерению;
 *  - база индекса — `base_*`, подписывается та, что пришла (FR-5.2);
 *  - направление динамики — `direction` показателя.
 *
 * Шесть состояний величины различимы (FR-9.3): `LEVEL_NOT_PUBLISHED`
 * строки не образует вовсе (районный ИФО промышленности — FR-4.3),
 * `NO_DATA` показывает «нет данных», `ZERO` — настоящий «0».
 *
 * Колонок «Место» и «РК» в этой волне нет (FR-8.1). Раскладка строки
 * задана одной константой `ROW_GRID`: вторая волна добавит в неё две
 * колонки, не переделывая макет (FR-8.2).
 */
import { TrendingDown, TrendingUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';
import { cn } from '../../../lib/cn';
import type { EconomyIndicator, EconomyValue } from '../../../types/analytics';

/**
 * Сетка строки: подпись + значение. Вторая волна (US-35.2 FR-9) добавляет
 * сюда колонки «Место» и «РК» — это правка одной строки, а не макета.
 */
const ROW_GRID = 'grid grid-cols-[1fr_auto] items-baseline gap-x-3';

const fmt1 = (v: number): string =>
  Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 }).format(v);

/** Величины-индексы сравниваются со 100 — только у них есть стрелка. */
const isIndex = (value: EconomyValue): boolean =>
  value.measure === 'IFO' || value.measure === 'INDEX';

export const EconomyIndicatorCard = ({
  indicator,
  testId,
}: {
  indicator: EconomyIndicator;
  testId: string;
}) => {
  const { t, i18n } = useTranslation();
  const isKz = i18n.language === 'kz';
  const loc = (ru: string | null, kz: string | null): string | null =>
    (isKz ? (kz ?? ru) : (ru ?? kz)) ?? null;

  // FR-4.3: величина, которую источник на этом уровне не публикует, строки
  // не образует — и уж точно не заменяется нулём.
  const rows = indicator.values.filter(
    (value) => value.state !== 'LEVEL_NOT_PUBLISHED',
  );

  const period = loc(
    indicator.period?.label_ru ?? null,
    indicator.period?.label_kz ?? null,
  );
  // База индекса одна на все составляющие — подписываем один раз (FR-5.2).
  const base = rows
    .map((value) => loc(value.base_ru, value.base_kz))
    .find((label) => label !== null);
  const source = loc(
    indicator.source?.name_ru ?? null,
    indicator.source?.name_kz ?? null,
  );
  const kind = rows.find((value) => value.index_kind != null)?.index_kind;

  return (
    <Card className="h-full shadow-card" data-testid={testId}>
      <CardHeader className="gap-1 pb-2">
        <CardTitle className="text-base leading-snug">
          {loc(indicator.name_ru, indicator.name_kz)}
        </CardTitle>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-text-faint">
          {period !== null && <span>{period}</span>}
          {indicator.period?.is_lagging === true && (
            <span
              className="rounded-full bg-status-warning/15 px-2 py-0.5 font-semibold text-status-warning"
              data-testid={`${testId}-lagging`}
            >
              {t('analytics.economy.blocks.lagging')}
            </span>
          )}
          {/* FR-9.2: значение вышестоящего уровня подписывается уровнем, а
              не выдаётся за значение территории. */}
          {!indicator.level_published && indicator.value_level !== null && (
            <span
              className="rounded-full bg-surface-muted px-2 py-0.5 font-semibold text-text-muted"
              data-testid={`${testId}-level`}
            >
              {t(`analytics.economy.blocks.levels.${indicator.value_level}`, {
                defaultValue: indicator.value_level,
              })}
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <p
            className="py-4 text-sm lowercase text-text-muted"
            data-testid="block-placeholder"
          >
            {t('common.noData')}
          </p>
        ) : (
          <>
            <div
              className={cn(
                ROW_GRID,
                'border-b border-surface-border pb-1.5 text-xs text-text-faint',
              )}
            >
              <span>{t('analytics.economy.blocks.columnIndicator')}</span>
              <span className="text-right">
                {t('analytics.economy.blocks.columnValue')}
              </span>
            </div>
            <ul>
              {rows.map((value) => {
                const label = loc(value.name_ru, value.name_kz);
                const unit = loc(value.unit_ru, value.unit_kz);
                const up = value.value !== null && value.value >= 100;
                const good =
                  indicator.direction === 'HIGHER_IS_WORSE' ? !up : up;
                const Arrow = up ? TrendingUp : TrendingDown;
                return (
                  <li
                    key={value.key}
                    className={cn(
                      ROW_GRID,
                      'border-b border-surface-border py-2 text-sm last:border-b-0',
                      // Составляющие показателя — с отступом под итогом.
                      value.part !== 'TOTAL' && 'pl-4 text-text-muted',
                    )}
                    data-testid={`${testId}-${value.key}`}
                  >
                    <span className="min-w-0 leading-snug">{label}</span>
                    <span className="flex shrink-0 items-baseline justify-end gap-1.5 whitespace-nowrap">
                      {value.state === 'NO_DATA' || value.value === null ? (
                        <span className="lowercase text-text-muted">
                          {t('common.noData')}
                        </span>
                      ) : (
                        <>
                          {isIndex(value) && (
                            <Arrow
                              className={cn(
                                'size-3.5 shrink-0 self-center',
                                good ? 'text-brand-600' : 'text-status-danger',
                              )}
                              aria-hidden="true"
                            />
                          )}
                          <b className="tabular-nums">{fmt1(value.value)}</b>
                          {unit !== null && (
                            <span className="text-xs text-text-muted">
                              {unit}
                            </span>
                          )}
                        </>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
            <div className="mt-2 space-y-0.5 text-xs text-text-faint">
              {base !== undefined && base !== null && (
                <p data-testid={`${testId}-base`}>
                  {t('analytics.economy.blocks.base', { value: base })}
                </p>
              )}
              {kind != null && (
                <p data-testid={`${testId}-kind`}>
                  {t(`analytics.economy.blocks.indexKinds.${kind}`, {
                    defaultValue: kind,
                  })}
                </p>
              )}
              {source !== null && (
                <p data-testid={`${testId}-source`}>
                  {t('analytics.economy.blocks.source', { value: source })}
                </p>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
